import {
    call,
    put,
    race,
    retry,
    select,
    spawn,
    take,
    takeEvery,
    takeLatest,
} from 'redux-saga/effects';

import constants, {
    NO_INFO_STOCK,
    adjustConfirmationDialog,
    adjustMaxAllowedProducts,
    cartSuccessStatuses,
    cartTypeConstants,
    cookieNames,
    paymentErrors,
    serverErrors,
    storageNames,
} from '@elab/core/utils/constants';
import { checkRts } from '@elab/core/utils/DeliveryModes/sagaHelpers';
import {
    actions as deliveryModesActions,
    selectors as deliverySelectors,
} from '@elab/components/connected/DeliveryModes/redux';
import addressActions from '@elab/core/store/address/actions';
import addressSelectors from '@elab/core/store/address/selectors';
import addressTypes from '@elab/core/store/address/types';
import { isAddressesEqual } from '@elab/core/utils/address/helpers';
import { createBulkAddToBagPayload } from '@elab/core/utils/helpers';
import {
    putBillingAddress,
    putDeliveryAddress,
} from '@elab/components/connected/customerAddresses/redux/saga';
import {
    canLimitativeOnThisPage,
    hasLimitationBrands,
    removeUrlParam,
} from '@elab/core/utils/cart/sagaHelpers';
import { getCartId, getUserId } from '@elab/core/utils/user/sagaHelpers';
import isEmpty from '@elab/core/utils/isEmpty';
import { actions as checkoutActions } from '@elab/core/store/checkout';
import { actions as accountActions } from '@elab/core/store/my-account';
import { roundToTwoDigits } from '@elab/core/utils';

import authActions from '../auth/actions';
import authTypes from '../auth/types';
import uiActions from '../ui/actions';
import uiSelectors from '../ui/selectors';
import uiTypes from '../ui/types';
import globalActions from '../global/actions';
import globalSelectors from '../global/selectors';
import { actions as paymentActions, types as paymentTypes } from '../payment';
import { actions as userActions, selectors as userSelectors } from '../user';
import analyticsActions from '../analytics/actions';
import freeSamplesActions from '../../components/connected/FreeSamples/redux/actions';
import { globalTypes } from '../types';
import rootActions from '../actions';
import { deleteCookie, getCookie, setCookie } from '../../utils/cookie';
import { application } from '../../utils/appData';
import * as storage from '../../utils/storage';
import analytics from '../../utils/analytics';
import logger from '../../utils/logger';
import { selectors as configSelectors } from '../config';
import { selectors as productSelectors } from '../product';
import { handleUseDeliveryAsBillingSetter } from '../checkout/saga';
import authCookiesManager from '../../utils/authCookiesManager';
import { getCurrentLocation } from '../../utils/address';
import productApi from '../product/api';

import { miniCartForcedMessageSources } from './forcedMessageSources';
import actions from './actions';
import selectors from './selectors';
import types from './types';
import cartApi from './api';

import { types as cartTypes } from '.';

const { DIGITAL, MIXED } = cartTypeConstants;

let DIALOG_NAME = adjustConfirmationDialog;

export function* loadCart(option = { skipCreation: false }) {
    yield put(actions.setLoading({ loading: true }));

    const userId = yield call(getUserId);
    const guid = yield select(selectors.getGuid);
    const code = yield select(selectors.getCode);
    const stocks = yield select(selectors.getStockValidation);
    const deliveryCountry = yield select(globalSelectors.getDeliveryCountry);

    const regularCartId = yield select(selectors.getRegularCartId);
    const isEgiftCardCartActive = !!(yield call(getEGCcartId));

    let cartIdentifier = userId ? code : guid;

    if (regularCartId && guid !== regularCartId && !isEgiftCardCartActive) {
        cartIdentifier = regularCartId;
        yield put(actions.setRegularCartId(''));
        yield put(actions.setGuid(cartIdentifier));
    }

    if (isEgiftCardCartActive) {
        cartIdentifier = guid;
    }

    try {
        if (cartIdentifier) {
            const data = yield call(
                cartApi.loadCart,
                userId,
                cartIdentifier,
                option.fields,
                stocks,
                isEgiftCardCartActive,
                deliveryCountry,
            );
            yield put(actions.setCartData(data));
            const checkoutConfig = yield select(configSelectors.getCheckout);
            if (checkoutConfig.useDeliveryAsBilling) {
                yield call(handleUseDeliveryAsBillingSetter);
            }
            yield put(paymentActions.setCartNotFound(false));
        } else if (!option.skipCreation) {
            yield call(createCart, userId);
        }
    } catch (e) {
        const { errors = [] } = e.cause;
        if (!option.skipCreation) {
            yield call(createCart, userId, errors[0]);
        }
    } finally {
        yield put(actions.setLoading({ loading: false }));
    }
}

export function* mergeCarts() {
    const anonCart = yield select(selectors.getData);
    const anonymousCartHasProducts = anonCart.entries && anonCart.entries.length;
    let guid = yield select(selectors.getGuid);
    const regularCartId = yield select(selectors.getRegularCartId);
    if (regularCartId && regularCartId !== guid) {
        guid = regularCartId;
    }
    const userId = yield select(userSelectors.getUserId);
    const mergeOldCart = yield select(configSelectors.mergeOldCart);
    try {
        let userCartGuid = null;
        if (!anonymousCartHasProducts || mergeOldCart) {
            const _userCart = yield call(cartApi.getMostRecentCart, userId);
            userCartGuid = _userCart.guid || null;
        }

        const mergedCart = yield call(cartApi.mergeCarts, guid, userCartGuid, userId);
        yield put(actions.setCartData(mergedCart));
        yield put(actions.cartBeenMerged());
        if (anonymousCartHasProducts && mergeOldCart) {
            yield put(actions.setIsProductsBeenMerged(true));
        }
    } catch (e) {
        yield call(logger.error, '============>', e);
    }
}

export function* checkCartEntries() {
    const entries = yield select(selectors.getEntries);
    const cartHasEntries = !!entries.length;
    const amountToPay = yield select(selectors.getAmountToPayValue);
    const cartDeliveryCost = yield select(selectors.getDeliveryCost);

    if (cartHasEntries || amountToPay === cartDeliveryCost.value) return;
    try {
        const userId = yield call(getUserId);
        const guid = yield select(selectors.getGuid);
        const code = yield select(selectors.getCode);

        yield retry(4, 400, function*() {
            const newCart = yield call(cartApi.loadCart, userId, userId ? code : guid);
            const newCartHasEntries = !!(newCart.entries || []).length;
            const newCartHasAmountToPay = !!(
                (newCart.amountToPay && newCart.amountToPay.value) ||
                0
            );

            if (newCartHasAmountToPay && !newCartHasEntries) {
                throw 'Cart with price and no entries';
            } else {
                yield put(actions.setCartData(newCart));
            }
        });
    } catch (e) {
        yield call(logger.error, '============>', e);
    }
}

export function* createCart(userId, error = {}) {
    try {
        if (error.reason === 'notFound') yield put(paymentActions.setCartNotFound(true));
        const cartData = yield call(cartApi.createCart, userId);
        yield put(actions.setCartData(cartData));
    } catch (e) {
        yield call(logger.error, e);
        const isAnonymous = authCookiesManager.isAnonymous();
        if (
            getCookie(cookieNames.tokenType) &&
            isAnonymous &&
            e.message === 'Unable to create cart (403)'
        ) {
            yield put(authActions.doLogout({ alreadyLoggedOut: true }));
        }
    }
}

export function* collectDataForAnalytics({
    code,
    quantity,
    product,
    dataFromView,
    totalPrice,
    entryQuantity,
    memberPrice,
    isThresholdPromotionApplied,
}) {
    const {
        masterBrand: { name: brand } = {},
        rangeName: range,
        name,
        categoryNameHierarchy: category,
        price: { value: price } = {},
        supplierDescription: supplier,
        baseProduct,
    } = product;

    const collectStockInfo = yield select(configSelectors.collectStockInfo);
    const collectAdditionalGAInfo = yield select(configSelectors.shouldCollectAdditionalGAInfo);

    const stockInfo = {};
    if (collectStockInfo) {
        stockInfo.inStock = product.stock && product.stock.stockLevelStatus === 'inStock';
        stockInfo.stockLevel = (product.stock && product.stock.stockLevel) || '';
    }

    let additionalGAProductData = {};
    if (collectAdditionalGAInfo) {
        const { price: { value, oldValue } = {} } = product;
        additionalGAProductData = {
            url: product.url || '',
            supplierDescription: product.supplierDescription || '',
            promoAmount:
                product.markdownPromotion && product.price && oldValue ? oldValue - value : 0,
            stockLevelData: (product.stock && product.stock.stockLevel) || '',
            inStockData: product.stock && product.stock.stockLevelStatus === 'inStock',
        };
    }

    const variant = product.variantValueCategories.length
        ? product.variantValueCategories[0].name
        : '';

    // sometimes category is set to empty string and spread overwrites correct category
    if (dataFromView && dataFromView.category === '') delete dataFromView.category;
    const otherPrice = product.otherPrices.filter(item => item.priceSource === 'MEMBER');
    const originalPrice = product && product.price ? product.price.value : 0;
    memberPrice = !isThresholdPromotionApplied
        ? (otherPrice && otherPrice[0] && otherPrice[0].value) || memberPrice
        : undefined;
    const productQuantity = entryQuantity || quantity;
    const salePrice = roundToTwoDigits(totalPrice.value / productQuantity);
    return {
        code,
        quantity,
        brand,
        range,
        name,
        category,
        price: salePrice,
        originalPrice: Number(originalPrice),
        memberPrice,
        variant,
        supplier,
        totalPrice,
        baseProduct,
        ...additionalGAProductData,
        ...dataFromView,
        ...stockInfo,
    };
}

export function* trackAnalyticsAddToCart(data) {
    const openedDialogName = yield select(uiSelectors.getDialogName);
    const product =
        openedDialogName === 'product-details-popup'
            ? yield select(productSelectors.getProductDetails, 'minipdp')
            : yield select(productSelectors.getProductDetails);

    const analyticsData = yield call(collectDataForAnalytics, {
        ...data,
        product: data && data.product ? data.product : product,
    });
    yield spawn(analytics.handleAddToCart, analyticsData);
}

export function* onAddOrUpdateCart({ payload }) {
    const entry = yield select(selectors.getEntry, payload.code);
    const openedDialogName = yield select(uiSelectors.getDialogName);

    if (!entry) {
        if (openedDialogName === 'gwp-choice-popup') {
            yield put(uiActions.unlockGwpFlow());
            yield put(uiActions.closeDialogByName('gwp-choice-popup'));
        }
        yield call(onAddToCart, { payload });
    } else {
        if (openedDialogName === 'product-details-popup') {
            yield put(uiActions.closeDialogByName('product-details-popup'));
        }

        yield call(onUpdateCart, {
            payload: {
                ...payload,
                entryNumber: entry.entryNumber,
                quantity: entry.quantity + parseInt(payload.quantity),
                fields: payload.fields || 'CART_REFRESH',
                showPopup: payload.showPopup || true,
            },
        });
    }
}

export function* updateDeliveryModes(oldCartType) {
    const cartType = yield select(selectors.getCartType);

    if (cartType !== oldCartType) {
        yield put(deliveryModesActions.loadDeliveryModes());
    }
}

export function* addToCart(product, fieldsValue, showNotification = true) {
    const userId = yield select(userSelectors.getUserId);
    const cartId = yield select(userId ? selectors.getCode : selectors.getGuid);
    const noNotification = product.noNotification;

    try {
        const oldCartType = yield select(selectors.getCartType);
        const key = yield call(storage.get, 'cart-id-swipe-to-buy', true);
        const { statusCode, quantityAdded, errors, missedGwpMessages } = yield call(
            cartApi.addToCart,
            userId,
            cartId,
            product,
        );

        if (showNotification && !noNotification) {
            yield put(
                uiActions.setMinicartPopoverData({
                    productCode: product.code,
                    quantityAdded,
                    statusCode,
                }),
            );
        }

        if (errors) {
            yield put(actions.setAddToCartError(true));
            const msg = errors[0].message;
            yield put(globalActions.setTemporaryMessage(msg));
            if (product.source === 'skinAdvisor') {
                yield spawn(analytics.handleSkinAdvisorError, msg);
            }
            return;
        }

        if (
            missedGwpMessages.length &&
            missedGwpMessages[0].products &&
            missedGwpMessages[0].products.length &&
            !key
        ) {
            const gwpProductCodes = missedGwpMessages[0].products;
            yield put(actions.persistGwpProductCodes(gwpProductCodes));
            yield put(
                uiActions.showGwpItems({
                    dialogName: 'gwp-choice-popup',
                    gwpProducts: gwpProductCodes,
                }),
            );
        }

        if (product.source === 'skinAdvisor') {
            yield spawn(analytics.handleSkinAdvisorAddToCart);
        }
        yield call(loadCart, {
            skipCreation: true,
            fields: fieldsValue || 'CART_REFRESH',
        });
        const entry = yield select(selectors.getEntry, product.code);
        const { totalPriceWithoutPromotions, totalPrice } = entry;
        const { otherPrices } = entry.product;
        const isThresholdPromotionApplied = yield call(
            checkThresholdPromotion,
            entry.product,
            entry.product.appliedPromotions,
        );
        const memberPromotion = otherPrices.find(price => price.priceSource === 'MEMBER');
        const memberPrice =
            !isThresholdPromotionApplied && memberPromotion ? memberPromotion.value : undefined;
        yield spawn(trackAnalyticsAddToCart, {
            code: product.code,
            quantity: quantityAdded,
            dataFromView: product.analytics,
            product: entry.product,
            totalPriceWithoutPromotions,
            totalPrice,
            memberPrice,
            isThresholdPromotionApplied,
        });

        yield call(updateDeliveryModes, oldCartType);
    } catch (e) {
        return e;
    }
}

export function* updateCart(entryNumber, quantity, fields, showPopup, analyticsPayload) {
    const userId = yield select(userSelectors.getUserId);
    const cartId = yield select(selectors.getCartId);

    let res = null;

    try {
        res = yield call(cartApi.updateCart, userId, cartId, entryNumber, quantity);
        const { statusCode, quantityAdded } = res;

        if (cartSuccessStatuses.indexOf(statusCode) > -1) {
            const loadCartOption = { skipCreation: true, fields };
            yield call(loadCart, loadCartOption);
            if (showPopup) {
                yield put(
                    uiActions.setMinicartPopoverData({
                        quantityAdded,
                        statusCode,
                    }),
                );
            }
        }
        const entries = yield select(selectors.getEntries);
        const { product, totalPriceWithoutPromotions, totalPrice } = entries[entryNumber];
        const { otherPrices } = product;
        const isThresholdPromotionApplied = yield call(
            checkThresholdPromotion,
            product,
            product.appliedPromotions,
        );
        const memberPromotion = otherPrices.find(price => price.priceSource === 'MEMBER');
        const memberPrice =
            !isThresholdPromotionApplied && memberPromotion ? memberPromotion.value : undefined;
        const analyticsData = yield call(collectDataForAnalytics, {
            code: product.code,
            quantity: quantityAdded,
            entryQuantity: quantity,
            product,
            dataFromView: analyticsPayload,
            totalPrice,
            totalPriceWithoutPromotions,
            memberPrice,
            isThresholdPromotionApplied,
        });
        yield spawn(analytics.handleUpdateCart, analyticsData);
    } catch (e) {
        if (e.reason === serverErrors.Unauthorized) {
            yield put(authActions.doLogout());
            return e;
        }
        yield call(loadCart, { skipCreation: true });
        yield call(logger.error, e);
    }
    return res;
}

export function* removeFromCart(code, dataFromView, removeConfig = {}) {
    const userId = yield select(userSelectors.getUserId);
    const cartId = yield select(userId ? selectors.getCode : selectors.getGuid);
    const entry = yield select(selectors.getEntry, code);

    if (entry) {
        try {
            const oldCartType = yield select(selectors.getCartType);
            yield call(cartApi.removeFromCart, userId, cartId, entry.entryNumber);
            const isEerMinicartConfig = yield select(configSelectors.getIsEerMinicart);
            if (!isEerMinicartConfig) {
                yield put(uiActions.toggleMinicartPopover(false));
            }

            if (removeConfig.isRemovalConfirmation) {
                yield put(uiActions.closeDialog());
            }

            yield put(actions.removeItemComplete(true));
            const entries = yield select(selectors.getEntries);
            const {
                product,
                totalPriceWithoutPromotions,
                totalPrice,
                appliedPromotions,
                quantity: entryQuantity,
            } = entries[entry.entryNumber];
            const { otherPrices } = product;
            const memberPromotion = otherPrices.find(price => price.priceSource === 'MEMBER');

            const isThresholdPromotionApplied = yield call(
                checkThresholdPromotion,
                product,
                appliedPromotions,
            );
            const memberPrice =
                !isThresholdPromotionApplied && memberPromotion ? memberPromotion.value : undefined;
            const analyticsData = yield call(collectDataForAnalytics, {
                code,
                quantity: entry.quantity,
                entryQuantity,
                product,
                dataFromView,
                totalPrice,
                totalPriceWithoutPromotions,
                memberPrice,
                isThresholdPromotionApplied,
            });
            yield spawn(analytics.handleRemoveFromCart, analyticsData);
            yield call(loadCart, { skipCreation: true });
            yield call(updateDeliveryModes, oldCartType);
        } catch (e) {
            yield call(logger.error, e);
            yield put(actions.setRemoveFromCartError(true));
        }
    } else {
        yield put(actions.setRemoveFromCartError(true));
    }
}

function checkThresholdPromotion(product, appliedPromotions) {
    const thresholdPromotion = product.promotions.find(promo => !!promo.minimumPurchaseAmount);
    let isThresholdPromotionApplied = !!thresholdPromotion;
    if (appliedPromotions && thresholdPromotion) {
        isThresholdPromotionApplied =
            appliedPromotions &&
            appliedPromotions.some(promo => promo.promoNumber === thresholdPromotion.code);
    }
    return isThresholdPromotionApplied;
}

export function* removeEntriesByProductCode(codes) {
    const userId = yield select(userSelectors.getUserId);
    const cartId = yield select(userId ? selectors.getCode : selectors.getGuid);

    try {
        yield call(cartApi.removeEntriesByProductCode, userId, cartId, codes);
        for (const code of codes) {
            const entry = yield select(selectors.getEntry, code);
            if (entry) {
                yield spawn(analytics.handleRemoveFromCart, {
                    code,
                    quantity: entry.quantity,
                    product: entry.product,
                });
            }
        }
    } catch (e) {
        yield call(logger.error, e);
    }
}

function mapBulkAddToBagPayloadPlp(productReferencesData, productData) {
    const orderEntries = [];
    const productReferences = productReferencesData;
    for (let product of productReferences) {
        orderEntries.push({
            code: product.target.code,
            quantity: product.quantity,
        });
    }
    return {
        orderEntries,
        code: productData.payload.productCode,
        productBundle: productData.payload.productBundle,
        quantityAdded: productData.payload.quantityAdded,
        swipeToBuy: false,
    };
}

export function* requestBundlesReferences(productData) {
    try {
        const productReferences = yield call(
            productApi.getReferences,
            productData.payload.productCode,
            productData.payload.referenceType,
        );
        const payload = mapBulkAddToBagPayloadPlp(productReferences.references, productData);
        yield call(onBulkAddToCart, { payload });
    } catch (error) {
        yield call(logger.error, error);
    }
}

export function* onBulkAddToCart({ payload }) {
    const { orderEntries } = payload;
    const userId = yield select(userSelectors.getUserId);
    const cartId = yield select(userId ? selectors.getCode : selectors.getGuid);
    const itemsToAdd = yield call(mapPayloadToCartItem, orderEntries);
    try {
        yield call(
            cartApi.bulkAddToCart,
            userId,
            cartId,
            {
                orderEntries: itemsToAdd,
            },
            !!payload.productBundle,
        );
        if (payload.productBundle) {
            yield call(loadCart, {
                skipCreation: true,
            });
            yield put(
                uiActions.setMinicartPopoverData({
                    productCode: payload.code,
                    quantityAdded: payload.quantityAdded,
                    statusCode: 'success',
                }),
            );
            if (!payload.swipeToBuy) {
                yield put(uiActions.openDialog({ name: 'product-cart-popup' }));
            }
        }
    } catch (error) {
        yield call(logger.error, error);
        yield put(actions.setAddToCartError(true));
    }
}

function mapPayloadToCartItem(payload) {
    let items = [];
    if (payload) {
        items = payload.map(item => {
            return { product: { code: item.code }, quantity: item.quantity };
        });
    }
    return items;
}

export function* onAddToCart(action) {
    const {
        code,
        quantity,
        popOverStockErrorLabel,
        showAsPopup,
        storeId,
        analytics,
        source = 'site',
        showNotification = true,
        fields,
        multiGwp,
        noNotification,
        closePopupIfOpened = true,
    } = action.payload;

    // Object for cartAPI
    const product = {
        code,
        quantity,
        storeId,
        source,
        multiGwp,
        noNotification,
    };
    // starts the task in the background
    const isLoadingCode = yield select(selectors.isLoadingCode, code);
    const isLoading = yield select(selectors.isLoading);
    if (isLoadingCode || isLoading) {
        return;
    }
    yield put(actions.setLoading({ loading: true, code }));

    const userId = yield select(userSelectors.getUserId);
    const cartId = yield select(userId ? selectors.getCode : selectors.getGuid);
    if (!cartId) {
        yield call(loadCart);
    }

    yield put(actions.setAddToCartError(false));
    yield put(actions.resetLimitativeBrands());

    try {
        const error = yield call(addToCart, { ...product, analytics }, fields);
        const openedDialogName = yield select(uiSelectors.getDialogName);

        if (openedDialogName === 'product-details-popup' && closePopupIfOpened) {
            yield put(uiActions.closeDialogByName('product-details-popup'));
        }
        if (openedDialogName === 'gwp-choice-popup') {
            yield take(uiTypes.UNLOCK_GWP_FLOW);
        }
        if (!error) {
            if (showNotification) {
                const hasSelectedGwp = yield select(selectors.getSelectedGwpItem);
                if (hasSelectedGwp.code) {
                    yield put(actions.updateSelectedGwp({}));
                    yield put(actions.persistGwpProductCodes([]));
                }
                showAsPopup
                    ? yield put(uiActions.openDialog({ name: 'product-cart-popup' }))
                    : yield put(uiActions.toggleMinicartPopover(true));
            }
        } else {
            yield put(actions.setAddToCartError(true));
            if (error.reason === serverErrors.Unauthorized) {
                yield put(authActions.doLogout());
            } else {
                // TODO TECH DEBT Check the error type
                yield put(actions.forceOutOfStock(code));
                yield call(checkForcedMessages, popOverStockErrorLabel);
            }
        }
    } catch (e) {
        yield call(logger.error, e);
        yield put(actions.setAddToCartError(true));
    } finally {
        yield put(actions.setLoading({ loading: false, code }));
        yield put(actions.loadCart());
    }
}

function* removeAllCartEntries() {
    const userId = yield select(userSelectors.getUserId);
    const cartId = yield select(userId ? selectors.getCode : selectors.getGuid);

    try {
        yield call(cartApi.removeAllCartEntries, userId, cartId);

        yield spawn(analytics.handleRemoveFromCart);
        deleteCookie(`${application.baseSiteId}-cart`);

        yield call(actions.initialize({ cleanData: true }));
    } catch (e) {
        return e;
    }
}

export function* onUpdateCart(action) {
    const {
        entryNumber,
        code,
        quantity,
        fields,
        showPopup = false,
        showAsPopup,
        analytics: analyticsPayload,
    } = action.payload;
    const entry = yield select(selectors.getEntry, code);
    const { maxOrderQuantity, minOrderQuantity } = entry.product;
    const canAddQuantity = quantity <= maxOrderQuantity;
    const useDialogCartNotifier = yield select(configSelectors.useDialogCartNotifier);
    // starts the task in the background
    const isLoadingCode = yield select(selectors.isLoadingCode, code);
    const isLoading = yield select(selectors.isLoading);
    if (isLoadingCode || isLoading) {
        return;
    }

    yield put(actions.setLoading({ loading: true, code }));
    yield put(actions.removeItemComplete(false));
    yield put(actions.resetLimitativeBrands());

    try {
        let res;
        const lowStockitems = yield select(selectors.getLowStockItems);
        const isInLowStockItems = lowStockitems.includes(code);
        if (quantity === 0 || quantity < minOrderQuantity) {
            yield call(removeFromCart, code, analyticsPayload);
            if (isInLowStockItems) {
                yield put(actions.removeFromLowStockItems(code));
                yield put(actions.clearHasLowStock());
                yield put(uiActions.clearStatusCode());
            }
        } else {
            const shouldPreventExceedQty = yield select(configSelectors.preventAddToCartOnExceed);
            if (shouldPreventExceedQty) {
                if (!canAddQuantity) {
                    if (useDialogCartNotifier) {
                        yield put(
                            uiActions.openDialog({
                                name: 'product-cart-popup--max-order-error',
                            }),
                        );
                    } else {
                        yield put(
                            uiActions.setMinicartPopoverData({
                                statusCode: 'maxOrderQuantityExceeded',
                            }),
                        );
                        yield put(uiActions.toggleMinicartPopover(true));
                        yield take(uiTypes.TOOGLE_MINICART_POPOVER_DATA);
                        yield put(
                            uiActions.setMinicartPopoverData({
                                statusCode: 'success',
                            }),
                        );
                    }
                    return;
                }
            }
            res = yield call(
                updateCart,
                entryNumber,
                quantity,
                fields,
                showPopup,
                analyticsPayload,
            );
            if (res && res.reason === serverErrors.Unauthorized) {
                return;
            }
        }

        const uiMiniCartData = yield select(uiSelectors.getMiniCartPopover);
        const uiStatus = uiMiniCartData.quantityAdded > 0 && quantity !== 0;

        if (showPopup) {
            yield put(uiActions.setMinicartPopoverData({ productCode: code }));

            showAsPopup
                ? yield put(uiActions.openDialog({ name: 'product-cart-popup' }))
                : yield put(uiActions.toggleMinicartPopover(uiStatus, !uiStatus));
        }

        const isSuccess = quantity === 0 || canAddQuantity || (res && res.statusCode === 'success');
        const isLowStock = res && res.statusCode === 'lowStock';
        yield put(actions.setHasLowStock(!isSuccess || isLowStock));
        yield put(actions.updateCartComplete(isSuccess));
        if (!isSuccess || isLowStock) {
            if (!isInLowStockItems) {
                yield put(actions.setLowStockItems(code));
            }
            const showMiniCartOnUnsuccessfulStatus = yield select(
                configSelectors.showMiniCartOnUnsuccessfulStatus,
            );
            if (showMiniCartOnUnsuccessfulStatus) {
                yield put(uiActions.toggleMinicartPopover(true));
            }
        }

        const isEmpty = yield select(selectors.isEmpty);
        const hasLowStock = yield select(selectors.getHasLowStock);
        if (isEmpty || !hasLowStock) {
            yield put(actions.clearHasLowStock());
            yield put(uiActions.clearStatusCode());
        }

        yield put(freeSamplesActions.setAllowedSamplesChanged(''));
        const resetAdjustedCartData = yield select(configSelectors.getResetAdjustedCartData);
        const haveOutOfStock = yield select(selectors.getHaveOutOfStock);
        const deliveryMethodCode = yield select(selectors.getDeliveryModeCode);
        const CnCEDeliveryMethodCode = yield select(configSelectors.getDeliveryModeForCnCE);
        if (
            resetAdjustedCartData &&
            (!haveOutOfStock || (deliveryMethodCode === CnCEDeliveryMethodCode && haveOutOfStock))
            // when cart is adjusted for cnce product & user increases quantity remove adjusted cart message to show oos message
        ) {
            yield put(actions.resetAdjustedCartData());
        }
        const mergeOldCart = yield select(configSelectors.mergeOldCart);
        if (mergeOldCart) {
            yield put(actions.setIsProductsBeenMerged(false));
        }
    } finally {
        yield put(actions.setLoading({ loading: false, code }));
    }
}

export function* onRemoveFromCart(action) {
    // starts the task in the background
    const isLoading = yield select(selectors.isLoading);
    if (isLoading) {
        return;
    }
    yield put(actions.setRemoveFromCartError(false));
    const { code, isDisabled, analytics, removeConfig } = action.payload;
    const lowStockitems = yield select(selectors.getLowStockItems);
    const isInLowStockItems = lowStockitems.includes(code);
    yield put(actions.setLoading({ loading: true, code }));
    yield put(actions.resetLimitativeBrands());
    try {
        yield call(removeFromCart, code, analytics, removeConfig);
        if (isInLowStockItems) {
            yield put(actions.removeFromLowStockItems(code));
            yield put(actions.clearHasLowStock());
            yield put(uiActions.clearStatusCode());
        }
        if (isDisabled) {
            return;
        }
        yield put(uiActions.toggleMinicartPopover(false, true));
    } finally {
        yield put(actions.setLoading({ loading: false, code }));
    }
}

export function* onSetCartData(action) {
    // starts the task in the background
    yield call(checkCartEntries);

    const data = action.payload;
    const baseSiteId = yield select(globalSelectors.getSite);
    yield call(deleteCookie, `${baseSiteId}-cart`);
    yield call(storage.remove, types.STORAGE_CODE);
    if (data && data.guid) {
        let guid = data.guid;
        const eGiftCardCartId = yield call(getEGCcartId);
        const regularCartId = yield select(selectors.getRegularCartId);

        if (eGiftCardCartId) {
            if (eGiftCardCartId === guid && regularCartId) {
                guid = regularCartId;
            }
            if (eGiftCardCartId !== guid) {
                yield put(actions.setRegularCartId(guid));
                yield put(actions.setGuid(eGiftCardCartId));
            }
        }
        yield call(setCookie, `${baseSiteId}-cart`, guid, 365);
    }

    if (data && data.code) {
        yield call(storage.set, types.STORAGE_CODE, data.code);
    }

    const expressPickupValidationMessages = yield select(
        selectors.getExpressPickupValidationMessage,
    );
    if (!expressPickupValidationMessages.length) {
        yield put(deliveryModesActions.setDeliveryBlocked(false));
    }

    const getDeliveryModeCode = yield select(selectors.getDeliveryModeCode);

    if (getDeliveryModeCode.toLowerCase().includes(constants.ccDeliveryName)) {
        const cartType = yield select(selectors.getCartType);
        const cartWithGiftCard = cartType === DIGITAL || cartType === MIXED;
        const deliveryAddress = yield select(selectors.getDeliveryAddress);

        if (cartWithGiftCard || !deliveryAddress.id) {
            yield put(deliveryModesActions.setDeliveryBlocked(true));
        }
    }

    if (getDeliveryModeCode) {
        yield call(outOfStockCheck);
        const { pathname = '' } = yield call(getCurrentLocation);

        const originAction = yield select(globalSelectors.getOriginAction);
        const immediatelyAdjustCart = yield select(configSelectors.getImmediatelyAdjustCart);
        const stopAdjustCartOnClose = yield select(configSelectors.getStopAdjustCartOnClose);
        const skipAutoAdjustmentOnPages = yield select(
            configSelectors.getSkipAutoAdjustmentOnPages,
        );

        const haveOutOfStock = yield select(selectors.getHaveOutOfStock);
        // const haveOutOfLimitProduct = yield select(selectors.getMaxAllowedProductExceeded);

        if (
            immediatelyAdjustCart &&
            !skipAutoAdjustmentOnPages.includes(pathname) &&
            haveOutOfStock
        ) {
            const adjustCartOnCartPage = yield select(configSelectors.getAdjusCartOnCartPage);
            const isNoStockInfo = yield select(selectors.getNoStock, NO_INFO_STOCK);
            const isSkipAutoRemovalOutOfStockProducts = yield select(selectors.getSkipAutoAdjust);
            const canAdjustCartOnCandCExpress = yield select(
                configSelectors.getAdjustCartOnClickAndCollectExpress,
            );

            const CnCEDeliveryMethodCode = yield select(configSelectors.getDeliveryModeForCnCE);
            const checkForCandCEflow =
                canAdjustCartOnCandCExpress || getDeliveryModeCode !== CnCEDeliveryMethodCode;

            const configDialogName = yield select(configSelectors.getAdjustDialogName);
            if (pathname !== constants.cartPageUrl) {
                DIALOG_NAME =
                    configDialogName === adjustMaxAllowedProducts
                        ? adjustMaxAllowedProducts
                        : DIALOG_NAME;
                yield call(checkForMaxAllowedQuantityForDelivery);
            }
            if (
                [paymentTypes.SET_PAYMENT_METHOD].includes(originAction) &&
                !isNoStockInfo &&
                DIALOG_NAME === adjustConfirmationDialog
            ) {
                yield put(uiActions.openDialog({ name: DIALOG_NAME }));
                const action = yield take([cartTypes.ADJUST_CART, uiTypes.CLOSE_DIALOG]);

                if (action.type === uiTypes.CLOSE_DIALOG && !stopAdjustCartOnClose) {
                    return yield put({
                        ...actions.adjustCart(),
                        payload: {
                            failureRedirectUrl: '/cart',
                            successRedirectUrl: '/cart',
                        },
                    });
                }
            } else if (
                adjustCartOnCartPage &&
                !isSkipAutoRemovalOutOfStockProducts &&
                checkForCandCEflow &&
                ![paymentTypes.ORDER_PLACED].includes(originAction)
            ) {
                yield put(actions.adjustCart());
            }
        }
    }
    const checkLimitativeBrands = yield select(selectors.getCheckLimitativeBrands);
    if (checkLimitativeBrands === true) {
        yield call(checkLimitationBrand);
    } else {
        yield put(actions.setCheckLimitativeBrands(true));
    }
    yield call(checkForcedMessages);

    yield call(checkRts, data);

    const isGuest = yield select(userSelectors.isAnonymous);
    if (isGuest && !data.paymentAddress && !data.deliveryAddress) {
        // clear the billing address from checkout store too when BE resets the cart
        yield put(checkoutActions.setAddressForBilling({}));
    }
}

function* checkForMaxAllowedQuantityForDelivery() {
    const { code } = yield select(selectors.getDeliveryMode);
    const pointOfService = yield select(selectors.getDeliveryPointOfService);
    const cartItems = yield select(selectors.getEntries);
    const maxQuantity = yield select(selectors.maxAllowedProductQuantity);
    const haveOutOfStock = yield select(selectors.getHaveOutOfStock);
    if (
        (maxQuantity || haveOutOfStock) &&
        (!code.includes(constants.ccDeliveryName) ||
            (code.includes(constants.ccDeliveryName) && pointOfService.pickupExpressEligible))
    ) {
        const hasProductQuantityThanMaxAllowed =
            cartItems.length && cartItems.some(({ quantity }) => quantity > maxQuantity);
        if (hasProductQuantityThanMaxAllowed || haveOutOfStock) {
            yield put(uiActions.openDialog({ name: DIALOG_NAME }));
            const action = yield take([cartTypes.ADJUST_CART, uiTypes.CLOSE_DIALOG]);

            return action.type === cartTypes.ADJUST_CART;
        }
    }
    return true;
}

export function* checkLimitationBrand(reloadCart = true) {
    const shouldCheckLimitationBrand = yield select(configSelectors.shouldCheckLimitationBrand);
    const userIsLoggedIn = yield select(userSelectors.getUserId);

    if (shouldCheckLimitationBrand && userIsLoggedIn) {
        const entries = yield select(selectors.getEntries);
        const hasLimitationBrand = yield call(hasLimitationBrands, entries);

        if (hasLimitationBrand) {
            yield put(actions.limitativeBrands());

            if (reloadCart) {
                yield take(types.SET_LIMITATIVE_BRANDS);
                const limitativeBrands = yield select(selectors.getLimitativeCartEntries);
                if (limitativeBrands.length) {
                    yield put(actions.setCheckLimitativeBrands(false));
                    yield call(loadCart, { skipCreation: true });
                }
            }
        } else {
            yield call(removeUrlParam);
        }
    }
}

export function* onLimitativeBrands() {
    const canLimitative = yield call(canLimitativeOnThisPage);
    if (!canLimitative) return;

    try {
        const userId = yield call(getUserId);
        const cartId = yield call(getCartId, userId);

        const limitativeCartEntries = yield call(cartApi.limitativeBrands, userId, cartId);

        yield put(actions.setLimitativeBrands(limitativeCartEntries));
    } catch (e) {
        yield spawn(logger.error, e);
    }
}

export function* getOverlimitedBrands() {
    const hasOverlimitedBrands = yield select(selectors.hasOverlimitedBrands);

    if (hasOverlimitedBrands) {
        try {
            const userId = yield call(getUserId);
            const cartId = yield call(getCartId, userId);

            const overlimitedBrands = yield call(cartApi.overlimitedBrands, userId, cartId);

            yield put(actions.setOverlimitedBrands(overlimitedBrands));
        } catch (e) {
            yield call(logger.error, e);
        }
    }
}

export function* outOfStockCheck() {
    const expressPickupValidation = yield select(selectors.getExpressPickupValidationMessage);
    if (expressPickupValidation.length > 0) {
        return;
    }

    const outOfStockEntries = yield select(selectors.getOutOfStockEntries);
    // const maxAllowedProductExceeded = yield select(selectors.entriesWithHigherQuantityThanAllowed);
    if (outOfStockEntries.length > 1) {
        yield put(actions.setOutOfStockEntriesNumber(outOfStockEntries.length));
    }
    const { code = '' } = yield select(selectors.getDeliveryMode);
    let hasOutOfStock = !!outOfStockEntries.length;
    // let hasMaxProduct = !!maxAllowedProductExceeded.length;
    // Don't show error msg for Click & Collect if store is not selected
    if (code.toLowerCase().includes(constants.ccDeliveryName)) {
        const { name } = yield select(selectors.getDeliveryPointOfService);
        if (!name) {
            hasOutOfStock = false;
            // hasMaxProduct = false;
        }
    }

    yield put(actions.setHaveOutOfStock(hasOutOfStock));
    // yield put(actions.setMaxAllowedExceeded(hasMaxProduct));
}

export function* checkForcedMessages(stockErrorLabel = false) {
    const skipForcedMessages = yield select(configSelectors.skipCartForcedMessages);
    if (skipForcedMessages) return;

    let forcedMessages;
    if (stockErrorLabel) {
        forcedMessages = [
            {
                text: stockErrorLabel,
                type: 'WARN',
            },
        ];
    } else {
        forcedMessages = yield select(selectors.getMessagesBySources, miniCartForcedMessageSources);
    }

    if (forcedMessages.length) {
        yield put(uiActions.setMiniCartForcedMessages(forcedMessages));
        yield put(uiActions.toggleMinicartPopover(true));
    } else {
        yield put(uiActions.removeMiniCartForcedMessages());
    }
}

export function* checkAddresses() {
    const applyDefaultAddressesOnLogin = yield select(configSelectors.applyDefaultAddressesOnLogin);

    if (applyDefaultAddressesOnLogin) {
        yield put(addressActions.loadAddresses());
        yield put(addressActions.loadBillingAddresses());
        const orderDeliveryAddress = yield select(selectors.getDeliveryAddress);
        const orderPaymentAddress = yield select(selectors.getPaymentAddress);
        const compareAddressConfig = yield select(configSelectors.getCompareAddress);

        yield take(addressTypes.SET_ADDRESSES);
        const userDefaultAddress = yield select(addressSelectors.getDefaultAddress);
        const userDefaultBillingAddress = yield select(addressSelectors.getDefaultBillingAddress);

        if (userDefaultAddress) {
            const isDeliveryAddressDefault = yield call(
                isAddressesEqual,
                orderDeliveryAddress,
                userDefaultAddress,
                compareAddressConfig.comparableProperties,
                compareAddressConfig.comparablePropertiesForCountry,
            );

            const shouldSetDefaultAddress = yield call(
                shouldSetDefaultAddressAsDelivery,
                orderDeliveryAddress,
            );

            if (!isDeliveryAddressDefault && shouldSetDefaultAddress) {
                yield call(putDeliveryAddress, userDefaultAddress.id);
            }
        }

        if (userDefaultBillingAddress) {
            const isPaymentAddressDefault = yield call(
                isAddressesEqual,
                orderPaymentAddress,
                userDefaultBillingAddress,
                compareAddressConfig.comparableProperties,
                compareAddressConfig.comparablePropertiesForCountry,
            );

            if (!isPaymentAddressDefault) {
                yield call(putBillingAddress, userDefaultBillingAddress.id);
            }
        }
    }

    yield put(actions.checkAddressesComplete());
}

export function* shouldSetDefaultAddressAsDelivery(address) {
    const currentDeliveryMode = yield select(selectors.getDeliveryModeCode);
    const usePostPointAddressAfterLogin = yield select(
        configSelectors.usePostPointAddressAfterLogin,
    );

    return !(
        currentDeliveryMode &&
        currentDeliveryMode.includes(constants.ppDeliveryType) &&
        address &&
        usePostPointAddressAfterLogin
    );
}

export function* onLoadCartData(action) {
    const loadCartOption = { skipCreation: false, ...action.payload };
    yield call(loadCart, loadCartOption);
    yield put(actions.setLoaded(true));
}

export function* onForceCartCodeChange({ payload }) {
    if (!payload.code) return;

    yield call(storage.set, types.STORAGE_CODE, payload.code);
    const skipInitialize = yield select(configSelectors.getSkipGlobalInitialize);
    if (skipInitialize) {
        return;
    }
    yield put(rootActions.init());
}

export function* onSetEmailAddress(action) {
    const { email } = action.payload;
    const userId = yield select(userSelectors.getUserId);
    const cartId = yield select(selectors.getCode);

    try {
        yield call(cartApi.setEmailAddress, userId, cartId, email);
    } catch (e) {
        yield call(logger.error, e);
    } finally {
        yield put(actions.setDataAttr('email', email));
    }
}

export function* onCheckRedirect({ payload }) {
    const cartCodeLocalStorage = yield call(storage.get, types.STORAGE_CODE);
    const cardCode = yield select(selectors.getCode);

    if (cartCodeLocalStorage === cardCode) {
        const warnings = yield select(selectors.getMessagesBySource, 'INSUFFICIENT_POINTS_UPDATE');
        if (warnings.length) {
            yield put(globalActions.redirect(payload.failureRedirectUrl));
            return;
        }

        const makeChangeDeliveryAddress = yield select(configSelectors.makeChangeDeliveryAddress);

        if (makeChangeDeliveryAddress) {
            const deliveryModeCode = yield select(selectors.getDeliveryModeCode);
            if (deliveryModeCode.toLowerCase().includes(constants.deliveryModeTypeHome)) {
                yield put(checkoutActions.setChangeDeliveryAddress({ status: false }));
            }
        }

        yield put(analyticsActions.checkoutCartEvents());
        const checkForEmptyStore = yield select(configSelectors.getDeliveryModeForCnCE);
        const deliveryMethodCode = yield select(selectors.getDeliveryModeCode);
        const CnCEDeliveryMethodCode = yield select(configSelectors.getDeliveryModeForCnCE);
        if (checkForEmptyStore && deliveryMethodCode === CnCEDeliveryMethodCode) {
            const cartEntries = yield select(selectors.getEntries);
            const ifStoreSelectionDone = !!cartEntries[0].deliveryPointOfService;
            const navigateTo = yield select(configSelectors.urlToNavigateIfStoreNotSelected);
            if (!ifStoreSelectionDone && navigateTo) {
                yield put(globalActions.goto(navigateTo, false));
            } else {
                yield put(globalActions.goto(payload.successRedirectUrl, false));
            }
        } else {
            yield put(globalActions.goto(payload.successRedirectUrl, false));
        }

        const resetAdjustedCartData = yield select(configSelectors.getResetAdjustedCartData);
        const haveOutOfStock = yield select(selectors.getHaveOutOfStock);

        if (resetAdjustedCartData && !haveOutOfStock) {
            yield put(actions.resetAdjustedCartData());
        }
    } else {
        yield put(authActions.doLogout());
    }
}

export function* onCartInitialize({ payload = {} }) {
    const { cleanData } = payload;
    const guidCookieName = `${application.baseSiteId}-cart`;

    if (cleanData) {
        yield call(storage.remove, types.STORAGE_CODE);
        yield call(deleteCookie, guidCookieName);
    }

    yield put(actions.setGuid(getCookie(guidCookieName)));
    yield put(actions.setCode(yield call(storage.get, types.STORAGE_CODE)));
}

export function* onAdjustCart({ payload = {} }) {
    const userId = yield call(getUserId);
    const cartId = yield select(userId ? selectors.getCode : selectors.getGuid);
    const { maxallowedsettlement } = payload;
    if (payload.dialogName) {
        yield put(uiActions.closeDialog());
    }
    let allAdjustedCartEntries;
    try {
        if (maxallowedsettlement) {
            allAdjustedCartEntries = yield call(cartApi.adjustCart, userId, cartId, {
                maxallowedsettlement,
            });
        } else {
            const { hasForbiddenStock, hasOnlyForbiddenEntryInCart } = yield select(
                selectors.getAdjustedCartData,
            );
            const deliveryCountry = yield select(globalSelectors.getDeliveryCountry);
            allAdjustedCartEntries = yield call(cartApi.adjustCart, userId, cartId, {
                hasForbiddenStock,
                hasOnlyForbiddenEntryInCart,
                deliveryCountry,
            });

            const immediatelyAdjustCart = yield select(configSelectors.getImmediatelyAdjustCart);
            const skipAutoAdjustmentOnPages = yield select(
                configSelectors.getSkipAutoAdjustmentOnPages,
            );
            const { pathname = '' } = yield call(getCurrentLocation);

            if (
                immediatelyAdjustCart &&
                (!skipAutoAdjustmentOnPages.includes(pathname) || payload.forceAdjust)
            ) {
                const adjustedOutOfStockEntries = [];
                const adjustedCartEntries = [];

                allAdjustedCartEntries.forEach(entry => {
                    entry.quantity === 0
                        ? adjustedOutOfStockEntries.push(entry)
                        : adjustedCartEntries.push(entry);
                });

                const isAdjustedCartEntriesPlural = adjustedCartEntries.length > 1;
                const isOutOfStockEntriesPlural = adjustedOutOfStockEntries.length > 1;

                yield put(
                    actions.setAdjustedCartData({
                        adjustedCartEntries,
                        adjustedOutOfStockEntries,
                        isAdjustedCartEntriesPlural,
                        isOutOfStockEntriesPlural,
                        hasForbiddenStock: false,
                        forbiddenStockPayload: {
                            adjustedForbiddenStock: hasForbiddenStock,
                        },
                    }),
                );
            }
        }
        yield call(loadCart, { skipCreation: true });

        const outOfStockEntries = yield select(selectors.getOutOfStockEntries);
        yield put(actions.updateCartComplete(!outOfStockEntries.length));
        const isEmpty = yield select(selectors.getIsEmpty);

        const redirectUrl = isEmpty ? payload.failureRedirectUrl : payload.successRedirectUrl;

        if (redirectUrl) {
            yield put(globalActions.redirect(redirectUrl));
        }
    } catch (e) {
        yield call(logger.error, e);
    }
}

export function* onRemoveOeProducts() {
    const userId = yield select(userSelectors.getUserId);
    const cartId = yield select(userId ? selectors.getCode : selectors.getGuid);
    try {
        yield call(cartApi.removeOEproducts, userId, cartId);
        yield call(loadCart, userId, cartId);
    } catch (e) {
        yield call(logger.error, e);
    }
}

export function* setDeliveryAddress(dto) {
    const userId = yield select(userSelectors.getUserId);
    const cartId = yield select(userId ? selectors.getCode : selectors.getGuid);
    yield call(cartApi.setDeliveryAddress, userId, cartId, dto);
}

export function* cleanDataAfterLogout() {
    yield put(actions.initialize({ cleanData: true }));
}

export function* toggleVoucherOnCart({ payload }) {
    const userId = yield select(userSelectors.getUserId);
    const cartGuid = yield select(selectors.getGuid);
    const showLoyaltyMessages = yield select(configSelectors.showLoyaltyMessages);
    yield put(actions.voucherIsLoading(true));
    const isAlreadyApplied = (yield select(selectors.getAppliedVouchers)).some(
        el => el.code === payload.code,
    );
    const isOrderFullFillmentNotAllowedWithLC = yield select(
        configSelectors.getIsOrderFullFillmentNotAllowedWithLC,
    );

    try {
        if (!isAlreadyApplied) {
            if (isOrderFullFillmentNotAllowedWithLC) {
                const appliedCheque = (yield select(userSelectors.getLoyaltyCheques)).find(
                    el => el.loyaltyNumber === payload.code,
                );
                const totalPrice = yield select(selectors.getTotalPrice);
                if (appliedCheque.value >= totalPrice.value) {
                    yield put(uiActions.scrollToTopOfPage());
                    return yield put(
                        paymentActions.setError(paymentErrors.orderCannotBeFullFilledWithLC),
                    );
                }
            }
            yield call(
                cartApi.applyGiftcardOrVoucher,
                userId,
                cartGuid,
                constants.voucherApiUrl,
                payload.code,
            );
        } else {
            yield call(
                cartApi.removeGiftcardOrVoucher,
                userId,
                cartGuid,
                payload.code,
                constants.voucherApiUrl,
            );
        }
        if (showLoyaltyMessages) {
            yield put(
                actions.setLoyaltyChequesMessage({
                    type: 'success',
                }),
            );
        }
    } catch (e) {
        const { message = '' } = (e.json && e.json.errors[0]) || {};
        yield put(
            userActions.setLoyaltyChequeError({
                loyaltyNumber: payload.code,
                message,
            }),
        );
        if (showLoyaltyMessages) {
            yield put(
                actions.setLoyaltyChequesMessage({
                    type: 'error',
                    message,
                }),
            );
        }
        yield put(actions.voucherIsLoading(false));
        yield put(uiActions.scrollToTopOfPage());
    } finally {
        yield call(loadCart, {
            skipCreation: true,
        });
        yield put(actions.voucherIsLoading(false));
        yield spawn(analytics.handleApplyBMVoucher, payload);
    }
}

export function* getEGCcartId() {
    const baseSiteId = yield select(globalSelectors.getSite);
    const cookieName = `${baseSiteId}${cookieNames.eGiftCardCartEnding}`;
    const eGiftCardCartId = yield call(getCookie, cookieName);
    return eGiftCardCartId;
}

export function* getHomeDeliveryDate({ payload }) {
    try {
        const estimatedDate = yield call(cartApi.getHomeDeliveryEstimatedDate, payload.code);

        yield put(actions.setHomeDeliveryDate(estimatedDate.message));
    } catch (e) {
        yield call(logger.error, e);
    }
}

export function* getCCExpressDeliveryDate({ payload }) {
    const lastUsedStore = yield call(storage.get, storageNames.lastUsedStore, true);
    let latitude = 0;
    let longitude = 0;

    if (lastUsedStore) {
        latitude = lastUsedStore.geoPoint && lastUsedStore.geoPoint.latitude;
        longitude = lastUsedStore.geoPoint && lastUsedStore.geoPoint.longitude;
    }

    try {
        const estimatedDate = yield call(
            cartApi.getCCEstimatedDeliveryDate,
            payload.code,
            latitude,
            longitude,
        );

        yield put(actions.setCCExpressDeliveryDate(estimatedDate.message));
    } catch (e) {
        yield call(logger.error, e);
    }
}

export function* getCCTraditionalDeliveryDate({ payload }) {
    let latitude = 0;
    let longitude = 0;
    const geoPoint = yield call(storage.get, storage.key.ccTraditionalDeliveryAddress, true);

    if (geoPoint) {
        latitude = geoPoint.latitude;
        longitude = geoPoint.longitude;
    }

    try {
        const estimatedDate = yield call(
            cartApi.getCCEstimatedDeliveryDate,
            payload.code,
            latitude,
            longitude,
            true,
        );

        yield put(actions.setCCTraditionalDeliveryDate(estimatedDate.message));
    } catch (e) {
        yield call(logger.error, e);
    }
}

export function* getPostPointDeliveryDate({ payload }) {
    let street = null;
    let postalCode = null;
    const deliveryInfo = yield call(storage.get, storage.key.ppDeliveryAddress, true);

    if (deliveryInfo) {
        street = deliveryInfo.street;
        postalCode = deliveryInfo.postalCode && deliveryInfo.postalCode.replace(/\s/g, '');
    }

    try {
        const estimatedDate = yield call(
            cartApi.getPostPointDeliveryEstimationDate,
            payload.code,
            street,
            postalCode,
        );

        yield put(actions.setPostPointDeliveryDate(estimatedDate.message));
    } catch (e) {
        yield call(logger.error, e);
    }
}

function* checkEgiftcardCart() {
    const eGiftCardCartId = yield call(getEGCcartId);
    if (!eGiftCardCartId) {
        return;
    }
    const guid = yield select(selectors.getGuid);
    if (guid !== eGiftCardCartId) {
        yield put(actions.setRegularCartId(guid));
        yield put(actions.setGuid(eGiftCardCartId));
    }
    yield call(loadCart);
}

function* restoreRegularCart({ payload }) {
    const shouldLoadCart = (payload && payload.shouldLoadCart) || false;
    const baseSiteId = yield select(globalSelectors.getSite);
    const cookieName = `${baseSiteId}${cookieNames.eGiftCardCartEnding}`;
    yield call(deleteCookie, cookieName);
    const cartId = yield select(selectors.getRegularCartId);
    yield put(actions.setGuid(cartId));
    shouldLoadCart && (yield call(loadCart));
}

function* manageSwipeToBuyPostOrder() {
    yield take(cartTypes.SET_CART_DATA);
    const key = yield call(storage.get, 'cart-id-swipe-to-buy', true);
    if (key) {
        yield put(actions.deleteCart());
        yield put(actions.restoreOldCartId());
    }
}

function* onRestoreOldCart() {
    const userId = yield select(userSelectors.getUserId);
    const key = yield call(storage.get, 'cart-id-swipe-to-buy', true);
    try {
        yield call(cartApi.restoreOldCart, userId, key);
        yield call(storage.remove, 'cart-id-swipe-to-buy');
        yield call(storage.remove, 'swipe-to-buy-cart-total');
    } catch (error) {
        // Show user error, we are not able to restore old cart ?
    } finally {
        yield call(loadCart, { skipCreation: true });
    }
}

function* onDeleteCart() {
    const userId = yield select(userSelectors.getUserId);
    const cartId = yield select(selectors.getGuid);
    yield call(cartApi.deleteCart, userId, cartId);
    yield put(actions.deleteCartComplete());
}

function* onCloseSwipeToBuyDialog() {
    yield put(actions.deleteCart());
    yield take(cartTypes.DELETE_CART_COMPLETE);
    yield put(paymentActions.resetRequiredCheck());
    yield put(accountActions.setValidationError(''));
    yield put(accountActions.setSelectedCard({ cardId: '' }));
    yield call(loadCart, { skipCreation: true });
    yield put(actions.swipeToBuyCloseProcessed());
}

function* onCloseSwipeToDialogRequested() {
    yield call(storage.remove, 'cart-id-swipe-previous');
    yield put(uiActions.closeDialog());
}

function* onSwipeToBuyRedirect({ payload }) {
    // yield put(uiActions.closeDialog());
    yield take(cartTypes.SWIPE_TO_BUY_CLOSE_PROCESSED);
    yield put(globalActions.redirect(payload.detail));
}

function* onShowSwipeToDialog({ payload }) {
    const userId = yield select(userSelectors.getUserId);
    yield put(accountActions.getSavedCard());
    const previousCartCode = yield select(selectors.getCode);
    const previousCartCodeGuid = yield select(selectors.getGuid);
    yield call(storage.set, 'cart-id-swipe-previous', previousCartCode, true);
    yield call(storage.set, 'cart-id-swipe-to-buy', previousCartCodeGuid, true);
    const quantity = yield select(productSelectors.getQuantity);
    const productData = yield select(productSelectors.getProductDetails);
    const isProductBundle = yield select(productSelectors.getProductBundleStatus);
    const addToCart = {
        ...{
            code: payload.productCode,
            quantity,
            showAsPopup: payload.showAsPopup,
            multiGwp: false,
            noNotification: true,
            showNotification: false,
            showPopup: false,
            fields: 'FULL',
        },
    };

    // Create Cart
    yield put(actions.createCart({ userId }));
    yield take(types.SET_CART_DATA);
    if (isProductBundle) {
        const bulkPayload = createBulkAddToBagPayload(productData, quantity);
        yield call(onBulkAddToCart, { payload: { ...bulkPayload, swipeToBuy: true } });
    } else {
        yield call(onAddOrUpdateCart, { payload: addToCart });
        yield take(types.SET_CART_DATA);
    }
    const isBlocked = yield select(deliverySelectors.getDeliveryBlocked);
    const swipeToBuyCartPrice = yield select(selectors.getTotalPrice);
    yield call(storage.set, 'swipe-to-buy-cart-total', swipeToBuyCartPrice.value, true);
    if (!isBlocked) {
        if (!payload.forceClick) {
            yield put(uiActions.openDialog({ name: payload.name }));
        }
    } else {
        yield call(showWarningPopUP, 'swipe-to-buy-order-info');
    }
}

function* showWarningPopUP(errorMsgtype) {
    yield put(uiActions.openDialog({ name: errorMsgtype }));
}

export function* toggleGwpItem({ payload = {} }) {
    const selectedGwpItem = yield select(selectors.getSelectedGwpItem);
    if (isEmpty(selectedGwpItem)) {
        yield put(actions.updateSelectedGwp(payload));
    }
    if (!isEmpty(selectedGwpItem)) {
        if (selectedGwpItem.code === payload.code) {
            yield put(actions.updateSelectedGwp({}));
        }
        if (selectedGwpItem.code !== payload.code) {
            yield put(actions.updateSelectedGwp(payload));
        }
    }
}

export function* resetPhoneDialogData() {
    const key = yield call(storage.get, 'cart-id-swipe-previous', true);
    if (key) {
        yield call(storage.remove, 'cart-id-swipe-previous');
        yield call(onCloseSwipeToBuyDialog);
    } else {
        const phoneNumber = yield select(selectors.getPhoneNumber);
        if (!phoneNumber.length && document.getElementsByName('phone').length) {
            const getInputMasksConfig = yield select(configSelectors.getInputMasksConfig);
            const defaultPhoneNumber =
                getInputMasksConfig.common.cartPhoneNumber.rules[0].config.defaultValue;
            document.getElementsByName('phone')[0].value = defaultPhoneNumber;
            yield put(actions.setDataAttr('phoneNumber', defaultPhoneNumber));
        }
    }
}

export function* removeBillingAdress() {
    const userId = yield select(userSelectors.getUserId);
    const cartId = yield select(userId ? selectors.getCode : selectors.getGuid);
    yield call(cartApi.removeBillingAddress, userId, cartId);
    yield put(actions.removeBillingAddressSuccess());
}

export function* checkForbiddenEntriesInStock({ payload = {} }) {
    const { onPageLoad } = payload;
    if (onPageLoad) {
        yield take(types.SET_CART_DATA);
    }

    const deliveryCountry = yield select(globalSelectors.getDeliveryCountry);
    const hasForbiddenEntries = yield select(
        selectors.getForbiddenEntriesByCountry,
        deliveryCountry,
    );

    if (hasForbiddenEntries.length) {
        yield call(updateForbiddenStockInAdjustCart, !!hasForbiddenEntries.length, onPageLoad);
        yield put(uiActions.openDialog({ name: adjustConfirmationDialog }));
        const action = yield take([types.ADJUST_CART, uiTypes.CLOSE_DIALOG]);
        if (action.type === uiTypes.CLOSE_DIALOG) return;
    }
    return true;
}

export function* updateForbiddenStockInAdjustCart(hasForbiddenEntries, onPageLoad) {
    const adjustedCartData = yield select(selectors.getAdjustedCartData);
    const entries = yield select(selectors.getEntries);
    adjustedCartData.hasForbiddenStock = hasForbiddenEntries;
    adjustedCartData.hasOnlyForbiddenEntryInCart = hasForbiddenEntries && entries.length === 1;
    adjustedCartData.forbiddenStockPayload = hasForbiddenEntries ? { onPageLoad } : {};
    yield put(actions.setAdjustedCartData(adjustedCartData));
}

export function* onSwipeToBuyPlaceOrder() {
    // TODO: Need to see how we can remove hard coding
    const previousTotal = yield call(storage.get, 'swipe-to-buy-cart-total', true);

    const deliveryMode = {
        code: 'wtctrMobilExpressSavedCard',
        name: 'Kredi Kartı',
        useCartGuid: true,
    };
    yield put(paymentActions.setPaymentMethod(deliveryMode));
    const { canContinue } = yield race({
        canContinue: take(paymentTypes.PAYMENT_METHOD_SET),
        cancel: take(paymentTypes.RESET_PAYMENT_METHOD),
    });
    yield take(types.SET_CART_DATA);
    const total = yield select(selectors.getTotalPrice);
    if (total.value === previousTotal) {
        if (canContinue) {
            yield put(paymentActions.placeSwipeToBuyOrder());
        } else {
            yield put(accountActions.setValidationError(paymentErrors.agreementsNotAccepted));
        }
    } else {
        yield put(accountActions.setValidationError(paymentErrors.cartNotSame));
    }
}

export function* onCreateCart({ payload }) {
    const { userId } = payload;
    yield call(createCart, userId);
}

export default [
    takeEvery([types.INITIALIZE, globalTypes.INITIALIZE], onCartInitialize),
    takeEvery(types.ADD_TO_CART, onAddToCart),
    takeEvery(types.UPDATE_CART, onUpdateCart),
    takeEvery(types.ADD_OR_UPDATE_CART, onAddOrUpdateCart),
    takeEvery(types.REMOVE_FROM_CART, onRemoveFromCart),
    takeEvery(types.REMOVE_ALL_CART_ENTRIES, removeAllCartEntries),
    takeLatest(types.SET_CART_DATA, onSetCartData),
    takeEvery(types.SET_EMAIL_ADDRESS, onSetEmailAddress),
    takeLatest(types.LOAD_CART_DATA, onLoadCartData),
    takeLatest(types.MERGE_CART, mergeCarts),
    takeEvery(types.CHECK_REDIRECT, onCheckRedirect),
    takeEvery(types.FORCE_CART_CODE_CHANGE, onForceCartCodeChange),
    takeEvery(types.ADJUST_CART, onAdjustCart),
    takeEvery(types.LIMITATIVE_BRANDS, onLimitativeBrands),
    takeEvery(types.LOAD_OVERLIMITED_BRANDS, getOverlimitedBrands),
    takeLatest(types.EXPRESS_PICKUP_VALIDATION, onRemoveOeProducts),
    takeEvery(authTypes.AFTER_LOGOUT, cleanDataAfterLogout),
    takeEvery(types.CHECK_ADDRESSES, checkAddresses),
    takeEvery(types.TOGGLE_VOUCHER_ON_CART, toggleVoucherOnCart),
    takeEvery(types.CHECK_EGIFTCARD_CART, checkEgiftcardCart),
    takeEvery(types.RESTORE_REGULAR_CART, restoreRegularCart),
    takeEvery(types.SELECT_GWP, toggleGwpItem),
    takeLatest(uiTypes.CLOSE_DIALOG, resetPhoneDialogData),
    takeEvery(types.LOAD_HOME_DELIVERY_DATE, getHomeDeliveryDate),
    takeEvery(types.LOAD_CLICK_AND_COLLECT_EXPRESS_DELIVERY_DATE, getCCExpressDeliveryDate),
    takeEvery(types.LOAD_CLICK_AND_COLLECT_DELIVERY_DATE, getCCTraditionalDeliveryDate),
    takeEvery(types.LOAD_POST_POINT_DELIVERY_DATE, getPostPointDeliveryDate),
    takeEvery(types.REMOVE_BILLING_ADDRESS, removeBillingAdress),
    takeEvery(types.CHECK_FORBIDDEN_ENTRIES_IN_STOCK, checkForbiddenEntriesInStock),
    takeEvery(types.OPEN_SWIPE_TO_BUY_DIALOG, onShowSwipeToDialog),
    takeEvery(types.CLOSE_SWIPE_TO_BUY_DIALOG, onCloseSwipeToDialogRequested),
    takeLatest(types.SWIPE_TO_BUY_PLACE_ORDER, onSwipeToBuyPlaceOrder),
    takeLatest(types.DELETE_CART, onDeleteCart),
    takeLatest(types.RESTORE_OLD_CART, onRestoreOldCart),
    takeLatest(types.MANAGE_POST_PROCESS_FOR_SWIPE_TO_BUY, manageSwipeToBuyPostOrder),
    takeEvery(types.CREATE_CART, onCreateCart),
    takeEvery(types.BULK_ADD_TO_CART, onBulkAddToCart),
    takeEvery(types.SWIPE_TO_BUY_REDIRECT, onSwipeToBuyRedirect),
    takeEvery(types.REQUEST_BUNDLE_REFERENCES, requestBundlesReferences),
];
